// ─── wordBlocksV3.js ──────────────────────────────────────────────────────────
// Condition Report v3 — OOXML "block kit" (spec §5.4). Pure functions that emit
// self-contained WordprocessingML fragments (header band, RAG hero, asset-summary,
// key-risk chips, dashboard, condition card, outlook) from the derived view model.
//
// This is the assembly kit shared by BOTH:
//   • the Stage-2 template generator (builds Condition_Report_v3.docx), and
//   • the Stage-3 fill engine (clones/fills these same blocks at export).
//
// Design finesse (pass 1): consistent cell padding, horizontal-only rules + zebra on
// data tables, section-heading underline rules with letter-spacing, a header band with
// muted labels / bold values and an orange accent, a RAG-hero sub-line, and an
// asset-summary header row. tblPr/pPr/tcPr children are emitted in schema order.
//
// Invariants:
//   • RAG fills + label text colour come from the single-source RAG_TOKENS. onFill is
//     contrast-tuned (white on red, dark on amber/green) so labels meet WCAG AA. These
//     NEW fills are v3-only — the legacy WordExport RAG palette is untouched.
//   • Cell shading (<w:shd>) is emitted in CT_TcPr order (tcW → gridSpan → tcBorders →
//     shd → vAlign); table props in CT_TblPr order (tblW → tblBorders → tblLayout →
//     tblCellMar → tblLook); paragraph props in CT_PPr order (pBdr → shd → spacing → jc).
//   • Pure string construction only (no XML parsing here); if a consumer ever parses
//     these attributes, use [^>/]* so a self-closing '/' can't be captured.
//   • Fragments carry NO w14:paraId/textId, so cloning them at export never duplicates
//     the template's ids.

import { RAG_TOKENS, RAG_RECOMMENDATION } from './areaConstants';

// ─── Design tokens (§5.1) — hex WITHOUT '#'; sizes are half-points (Word w:sz) ──
export const V3 = {
  purple:    '5B2D82', // brand — section headings, chips, heading rule
  orange:    'E8871B', // brand accent — callout + header-band rule
  slate:     '394553', // header band / table header / card header background
  slateSoft: 'C9CED6', // muted label text on the slate band
  line:      'D8DCE1', // table hairlines
  zebra:     'F5F7F8', // alternating data-table row tint
  callout:   'F7F6F4', // recommendation callout fill
  paper:     'FFFFFF',
  ink:       '1A1A1A', // body / dark label text on light RAG fills
  muted:     '8A8F98', // category / secondary text
  benefitInk: '176B3F', // dark green for the "Good news" savings line (AA on tint)
  benefitTint: 'EAF5EE', // light green savings-callout fill
  font:      'Century Gothic',
  pageWidthDxa: 9900, // 11906 pgSz − 1080 left − 926 right (Section-2 margins)
};

// Default inner cell padding (twips) — the single biggest "designed" lift over Word's
// tight defaults. top/bottom give vertical air; left/right indent text off the rule.
const CELL_MAR = '<w:tblCellMar>'
  + '<w:top w:w="40" w:type="dxa"/><w:left w:w="108" w:type="dxa"/>'
  + '<w:bottom w:w="40" w:type="dxa"/><w:right w:w="108" w:type="dxa"/></w:tblCellMar>';

// ─── Primitives ────────────────────────────────────────────────────────────────

// XML-escape for both text nodes and attribute values.
export function esc(s) {
  return String(s == null ? '' : s)
    .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

// Run properties. sz is half-points (20 = 10pt); track is character spacing in
// twentieths of a point (20 = 1pt) for refined small-caps labels.
export function rpr({ font = V3.font, sz, bold, italic, caps, color, track } = {}) {
  let s = `<w:rFonts w:ascii="${font}" w:hAnsi="${font}"/>`;
  if (bold) s += '<w:b/>';
  if (italic) s += '<w:i/>';
  if (caps) s += '<w:caps/>';
  if (color) s += `<w:color w:val="${color}"/>`;
  if (track != null) s += `<w:spacing w:val="${track}"/>`;
  if (sz) s += `<w:sz w:val="${sz}"/><w:szCs w:val="${sz}"/>`;
  return `<w:rPr>${s}</w:rPr>`;
}

// A single text run.
export function run(text, opts) {
  return `<w:r>${rpr(opts)}<w:t xml:space="preserve">${esc(text)}</w:t></w:r>`;
}

// A paragraph wrapping pre-built run XML. pPr children emitted in CT_PPr order:
// keepNext → keepLines → pBdr → shd → spacing → jc. Order is schema-enforced; Word
// shows a repair prompt if it's wrong, even though the XML stays well-formed.
export function para(runsXml, { align, shd, before, after, rule, keepNext, keepLines } = {}) {
  const pp = [];
  if (keepNext) pp.push('<w:keepNext/>');
  if (keepLines) pp.push('<w:keepLines/>');
  if (rule) pp.push(`<w:pBdr><w:bottom w:val="single" w:sz="${rule.sz || 6}" w:space="${rule.space != null ? rule.space : 4}" w:color="${rule.color || V3.purple}"/></w:pBdr>`);
  if (shd) pp.push(`<w:shd w:val="clear" w:color="auto" w:fill="${shd}"/>`);
  if (before != null || after != null) {
    pp.push(`<w:spacing${before != null ? ` w:before="${before}"` : ''}${after != null ? ` w:after="${after}"` : ''}/>`);
  }
  if (align) pp.push(`<w:jc w:val="${align}"/>`);
  const ppr = pp.length ? `<w:pPr>${pp.join('')}</w:pPr>` : '';
  return `<w:p>${ppr}${runsXml || ''}</w:p>`;
}

// A table cell. tcPr children in CT_TcPr order: tcW → gridSpan → tcBorders → shd →
// vAlign. `borders` is pre-built <w:tcBorders>. Empty cell gets a bare <w:p/>.
export function cell(paras, { w, shd, valign, gridSpan, borders } = {}) {
  let tc = w != null ? `<w:tcW w:w="${w}" w:type="dxa"/>` : '<w:tcW w:w="0" w:type="auto"/>';
  if (gridSpan) tc += `<w:gridSpan w:val="${gridSpan}"/>`;
  if (borders) tc += borders;
  if (shd) tc += `<w:shd w:val="clear" w:color="auto" w:fill="${shd}"/>`;
  if (valign) tc += `<w:vAlign w:val="${valign}"/>`;
  let body = Array.isArray(paras) ? paras.filter(Boolean).join('') : (paras || '');
  if (!body) body = '<w:p/>';
  return `<w:tc><w:tcPr>${tc}</w:tcPr>${body}</w:tc>`;
}

// A table row from pre-built cell XML. trPr children in CT_TrPr order: cantSplit →
// trHeight. cantSplit stops Word splitting this row across a page.
export function row(cellsXml, { heightTwips, cantSplit } = {}) {
  const tp = [];
  if (cantSplit) tp.push('<w:cantSplit/>');
  if (heightTwips) tp.push(`<w:trHeight w:val="${heightTwips}" w:hRule="atLeast"/>`);
  const trPr = tp.length ? `<w:trPr>${tp.join('')}</w:trPr>` : '';
  return `<w:tr>${trPr}${Array.isArray(cellsXml) ? cellsXml.join('') : cellsXml}</w:tr>`;
}

// Full single-line box (all sides + inside) — for the bordered condition card.
export function borderBox(color = V3.line, sz = 4) {
  const e = (n) => `<w:${n} w:val="single" w:sz="${sz}" w:space="0" w:color="${color}"/>`;
  return `<w:tblBorders>${e('top')}${e('left')}${e('bottom')}${e('right')}${e('insideH')}${e('insideV')}</w:tblBorders>`;
}

// Horizontal hairlines only (top / bottom / inside-H) — cleaner "data table" look for
// the dashboard, outlook and asset-summary tables (no vertical rules).
export function borderRows(color = V3.line, sz = 4) {
  const e = (n) => `<w:${n} w:val="single" w:sz="${sz}" w:space="0" w:color="${color}"/>`;
  const none = (n) => `<w:${n} w:val="none" w:sz="0" w:space="0" w:color="auto"/>`;
  return `<w:tblBorders>${e('top')}${none('left')}${e('bottom')}${none('right')}${e('insideH')}${none('insideV')}</w:tblBorders>`;
}

// A table from row XML + column widths (dxa). tblPr children in CT_TblPr order:
// tblW → tblBorders → tblLayout → tblCellMar → tblLook. `borders` is pre-built.
export function table(rowsXml, colWidths, { borders } = {}) {
  const total = colWidths.reduce((a, b) => a + b, 0);
  const grid = colWidths.map((w) => `<w:gridCol w:w="${w}"/>`).join('');
  const tblPr = '<w:tblPr>'
    + `<w:tblW w:w="${total}" w:type="dxa"/>`
    + `${borders || ''}`
    + '<w:tblLayout w:type="fixed"/>'
    + CELL_MAR
    + '<w:tblLook w:val="04A0" w:firstRow="1" w:lastRow="0" w:firstColumn="1" w:lastColumn="0" w:noHBand="0" w:noVBand="1"/>'
    + '</w:tblPr>';
  return `<w:tbl>${tblPr}<w:tblGrid>${grid}</w:tblGrid>${(Array.isArray(rowsXml) ? rowsXml : [rowsXml]).join('')}</w:tbl>`;
}

// A hard page break in its own paragraph.
export function pageBreak() {
  return '<w:p><w:r><w:br w:type="page"/></w:r></w:p>';
}

// Spacer paragraph (empty), giving vertical rhythm between blocks.
export function spacer(after = 160) {
  return `<w:p><w:pPr><w:spacing w:after="${after}"/></w:pPr></w:p>`;
}

// A section heading — brand purple, tracked, with a thin purple underline rule that
// ties the sections into one system. Doubles as the Stage-3 locate anchor.
export function sectionHeading(text) {
  return para(run(text, { sz: 26, bold: true, color: V3.purple, track: 10 }),
    { before: 260, after: 120, rule: { color: V3.purple, sz: 6, space: 4 } });
}

// ─── Blocks (spec §5.4) ─────────────────────────────────────────────────────────

// Header band — full-width slate cell, orange rule beneath. Metadata lives in
// coverMeta() below the band (Jason's cover layout), not inside the band.
export function headerBand(meta = {}) {
  const W = V3.pageWidthDxa;
  const accent = `<w:tcBorders><w:bottom w:val="single" w:sz="18" w:space="0" w:color="${V3.orange}"/></w:tcBorders>`;
  const c = cell([
    para(run('CONDITION REPORT', { sz: 18, bold: true, caps: true, track: 30, color: V3.slateSoft }), { after: 60 }),
    para(run(meta.siteName || 'Site', { sz: 34, bold: true, color: 'FFFFFF' })),
  ], { w: W, shd: V3.slate, valign: 'center', borders: accent });
  return table([row([c], { heightTwips: 1150 })], [W]);
}

// Client / Assessed / Assessor, beneath the band on the cover page. Blank values omit
// their line entirely rather than emitting a dangling label.
export function coverMeta(meta = {}) {
  const line = (label, val) => (val
    ? para(run(`${label}  `, { sz: 15, caps: true, track: 20, color: V3.muted })
      + run(val, { sz: 18, bold: true, color: V3.ink }), { after: 40 })
    : '');
  return line('Client', meta.clientName) + line('Assessed', meta.inspectionDate) + line('Assessor', meta.assessorName);
}

// The hero site photo, centred. runXml is a pre-built photoTokenRun; falsy → no
// paragraph at all, so a project with no cover photo leaves no gap.
export function coverPhoto(runXml) {
  if (!runXml) return '';
  return para(runXml, { align: 'center', before: 240 });
}

// The cover letter: salutation, an authored opener, then the deterministic overall
// summary verbatim. No second narrative engine — summaryLibrary already owns the prose.
export function coverLetter({ salutation, opener, summary } = {}) {
  let out = para(run(salutation || 'Dear Sir/Madam,', { sz: 20, color: V3.ink }), { after: 160 });
  if (opener) out += para(run(opener, { sz: 20, color: V3.ink }), { after: 120 });
  if (summary) out += para(run(summary, { sz: 20, color: V3.ink }), { after: 160 });
  return out;
}

// RAG hero — full-width single shaded cell: large centred rating + a smaller sub-line
// stating the recommendation. onFill picks white/dark for AA. NEW v3 mechanic.
export function ragHero(rag) {
  const t = RAG_TOKENS[rag];
  const label = t ? `OVERALL CONDITION — ${t.label.toUpperCase()}` : 'OVERALL CONDITION — NOT ASSESSED';
  const on = t ? t.onFill : 'FFFFFF';
  const paras = [
    para(run(label, { sz: 40, bold: true, caps: true, track: 20, color: on }),
      { align: 'center', before: 200, after: t ? 40 : 200 }),
  ];
  if (t) paras.push(para(run((RAG_RECOMMENDATION[rag] && RAG_RECOMMENDATION[rag].hero) || '', { sz: 20, caps: true, track: 30, color: on }), { align: 'center', after: 200 }));
  return table([row([cell(paras, { w: V3.pageWidthDxa, shd: t ? t.fill : V3.slate, valign: 'center' })], { heightTwips: 1000 })], [V3.pageWidthDxa]);
}

// Asset summary — header row (Rating | Count) + one RAG-shaded stat row per rating.
export function assetSummary(stats = {}) {
  const W = V3.pageWidthDxa; const c1 = Math.round(W * 0.72); const c2 = W - c1;
  const hdr = row([
    cell(para(run('Rating', { sz: 18, bold: true, color: 'FFFFFF' })), { w: c1, shd: V3.slate, valign: 'center' }),
    cell(para(run('Count', { sz: 18, bold: true, color: 'FFFFFF' }), { align: 'center' }), { w: c2, shd: V3.slate, valign: 'center' }),
  ]);
  const rows = ['red', 'amber', 'green'].map((k) => {
    const t = RAG_TOKENS[k];
    const left = cell(para(run(`${t.label} — ${RAG_RECOMMENDATION[k].action}`, { sz: 20, bold: true, color: t.onFill })),
      { w: c1, shd: t.fill, valign: 'center' });
    const right = cell(para(run(String(stats[k] || 0), { sz: 22, bold: true, color: V3.ink }), { align: 'center' }),
      { w: c2, valign: 'center' });
    return row([left, right]);
  });
  return table([hdr, ...rows], [c1, c2], { borders: borderRows() });
}

// Key-risk chips — one shaded (brand purple, white = 9.8:1) cell per risk in a single
// row. Empty string if no risks. Caps at 5 for layout.
export function keyRiskChips(risks = []) {
  const labels = (risks || []).map((r) => r && r.label).filter(Boolean).slice(0, 5);
  if (!labels.length) return '';
  const W = V3.pageWidthDxa; const cw = Math.floor(W / labels.length);
  const widths = labels.map((_, i) => (i === labels.length - 1 ? W - cw * (labels.length - 1) : cw));
  const cells = labels.map((l, i) => cell(
    para(run(l, { sz: 18, bold: true, color: 'FFFFFF' }), { align: 'center', before: 60, after: 60 }),
    { w: widths[i], shd: V3.purple, valign: 'center' }
  ));
  return table([row(cells)], widths);
}

// Higgins Recommendation — accent callout: single cell, thick orange left rule, light
// fill, tracked caps label + strategy text.
export function recommendationCallout(text) {
  const W = V3.pageWidthDxa;
  const leftRule = `<w:tcBorders><w:left w:val="single" w:sz="24" w:space="0" w:color="${V3.orange}"/></w:tcBorders>`;
  const c = cell([
    para(run('Higgins Recommendation', { sz: 18, bold: true, caps: true, track: 20, color: V3.orange }), { after: 60 }),
    para(run(text || '', { sz: 20, color: V3.ink })),
  ], { w: W, shd: V3.callout, valign: 'center', borders: leftRule });
  return table([row([c], { heightTwips: 700 })], [W]);
}

// Benefit / savings line for the executive summary — a positive green-accented callout:
// "Good news — we identified N surfaces that do not need repainting in the near term."
// Green left rule + light-green tint; the "Good news" lead is a legible dark green (AA).
// Empty string when count < 1 (no benefit to claim). `noun` defaults to "surfaces".
export function benefitCallout(count, opts = {}) {
  const n = Number(count) || 0;
  if (n < 1) return '';
  const noun = opts.noun || 'surfaces';
  const W = V3.pageWidthDxa;
  const rule = `<w:tcBorders><w:left w:val="single" w:sz="24" w:space="0" w:color="${RAG_TOKENS.green.fill}"/></w:tcBorders>`;
  const msg = `we identified ${n} ${noun} that do not need repainting in the near term.`;
  const c = cell([
    para(run('Good news — ', { sz: 19, bold: true, color: V3.benefitInk }) + run(msg, { sz: 19, color: V3.ink })),
  ], { w: W, shd: V3.benefitTint, valign: 'center', borders: rule });
  return table([row([c], { heightTwips: 500 })], [W]);
}

// Priority-grouped recommendation — a stack of colour-coded callouts, one per RAG
// tier present. Each has a thick left rule in the RAG fill (the colour cue), a
// bold verb heading (Repaint ASAP / Consider Painting / No Painting Required), a
// plain-language lead, and the areas in that band. Verb/lead text stay dark
// (slate/ink) so they read on the light fill — the RAG colour lives in the rule,
// not the text. tiers: [{rag, verb, lead, areas}].
export function priorityRecommendation(tiers = []) {
  const W = V3.pageWidthDxa;
  return (tiers || []).map((t) => {
    const rag = RAG_TOKENS[t.rag];
    const rule = `<w:tcBorders><w:left w:val="single" w:sz="24" w:space="0" w:color="${rag ? rag.fill : V3.slate}"/></w:tcBorders>`;
    const areasLine = (t.areas && t.areas.length) ? `Areas: ${t.areas.join(', ')}` : '';
    const paras = [
      para(run(t.verb || '', { sz: 19, bold: true, caps: true, track: 15, color: V3.slate }), { after: 40 }),
      para(run(t.lead || '', { sz: 18, color: V3.ink }), { after: areasLine ? 40 : 0 }),
    ];
    if (areasLine) paras.push(para(run(areasLine, { sz: 17, italic: true, color: V3.muted })));
    if (t.identified && t.identified.length) {
      paras.push(para(
        run('Higgins identified: ', { sz: 17, bold: true, italic: true, color: V3.muted })
        + run(t.identified.join('  ·  '), { sz: 17, italic: true, color: V3.muted })
      ));
    }
    const c = cell(paras, { w: W, shd: V3.callout, valign: 'center', borders: rule });
    return table([row([c])], [W]) + spacer(100);
  }).join('');
}

// A header row of white-on-slate label cells.
function headerRow(labels, widths) {
  const cells = labels.map((l, i) => cell(
    para(run(l, { sz: 18, bold: true, color: 'FFFFFF' }), { align: i === 0 ? undefined : 'center' }),
    { w: widths[i], shd: V3.slate, valign: 'center' }
  ));
  return row(cells);
}

// Dashboard — Area | Condition (RAG-shaded) | Action. rows: {area, rag, action}.
// Alternating rows carry a light zebra tint on the non-condition cells for legibility.
export function dashboardTable(rows = []) {
  const W = V3.pageWidthDxa;
  const cA = Math.round(W * 0.46); const cC = Math.round(W * 0.24); const cAc = W - cA - cC;
  const widths = [cA, cC, cAc];
  const hdr = headerRow(['Area', 'Condition', 'Action'], widths);
  const body = (rows || []).map((r, i) => {
    const t = RAG_TOKENS[r.rag];
    const z = i % 2 === 1 ? V3.zebra : undefined;
    return row([
      cell(para(run(r.area || '', { sz: 18, color: V3.ink })), { w: cA, shd: z, valign: 'center' }),
      cell(para(run(t ? t.label : '', { sz: 18, bold: true, color: t ? t.onFill : V3.ink }), { align: 'center' }),
        { w: cC, shd: t ? t.fill : z, valign: 'center' }),
      cell(para(run(r.action || '', { sz: 18, color: V3.ink })), { w: cAc, shd: z, valign: 'center' }),
    ]);
  });
  return table([hdr, ...body], widths, { borders: borderRows() });
}

// Separator between side-by-side evidence photos. Two spaces at 9pt ≈ 110,000 EMU —
// the row's width budget (see plan geometry) leaves slack for two of these, so a
// three-photo strip can never wrap to a second line.
const PHOTO_GUTTER = () => run('  ', { sz: 18 });

// A bordered condition card: header (surface + RAG chip) → notes → optional photo
// strip (up to 3 photos inline). Every row cantSplit; every paragraph keepNext except
// those in the final row, so Word moves the whole card to the next page rather than
// splitting it.
//
// P3: item.outcome === 'excluded' renders the RAG chip as a neutral "Excluded" pill
// (V3.line fill, V3.ink text) rather than a green "Good" chip — green means "we
// looked and it's fine", excluded means "this isn't in scope", and they must not
// look alike. item.reason (set for both 'excluded' and 'scheme' outcomes) renders
// as an italic muted line under the substrate name.
//
// The card carries no substrate-family label ("LINED", "MASONRY"): it named the
// paint family rather than anything the client acts on, and read as clutter under
// the surface name. familyForSubstrate() still drives defect selection — only the
// printed line is gone.
export function conditionCard(item = {}) {
  const W = V3.pageWidthDxa; const cN = Math.round(W * 0.74); const cR = W - cN;
  const isExcluded = item.outcome === 'excluded';
  const t = RAG_TOKENS[item.rag];
  const photos = (item.photos || []).filter(Boolean);
  const hasPhotos = photos.length > 0;

  // keepNext binds a row to the next one. The LAST row must not carry it.
  const headerKeep = true;
  const notesKeep = hasPhotos;

  const chipLabel = isExcluded ? 'Excluded' : (t ? t.label : '—');
  const chipFill = isExcluded ? V3.line : (t ? t.fill : V3.slate);
  const chipColor = isExcluded ? V3.ink : (t ? t.onFill : 'FFFFFF');

  const header = row([
    cell(para(run(item.substrate || '', { sz: 20, bold: true, color: 'FFFFFF' }), { keepNext: headerKeep }),
      { w: cN, shd: V3.slate, valign: 'center' }),
    cell(para(run(chipLabel, { sz: 20, bold: true, color: chipColor }), { align: 'center', keepNext: headerKeep }),
      { w: cR, shd: chipFill, valign: 'center' }),
  ], { cantSplit: true });

  const notesParas = [];
  if (item.reason) notesParas.push(para(run(item.reason, { sz: 16, italic: true, color: V3.muted }), { after: 60, keepNext: notesKeep }));
  notesParas.push(para(run(item.note || '', { sz: 18, color: V3.ink }), { keepNext: notesKeep }));
  if (item.action) notesParas.push(para(run(`Recommended: ${item.action}`, { sz: 18, bold: true, color: V3.purple }), { before: 60, keepNext: notesKeep }));
  const notes = row([cell(notesParas, { w: W, gridSpan: 2 })], { cantSplit: true });

  const rowsXml = [header, notes];
  if (hasPhotos) {
    const strip = photos.join(PHOTO_GUTTER());
    rowsXml.push(row([cell(para(strip, { align: 'center' }), { w: W, gridSpan: 2 })], { heightTwips: 2300, cantSplit: true }));
  }
  return table(rowsXml, [cN, cR], { borders: borderBox() });
}

// Outlook — Area | Current Condition (RAG-shaded) | Recommendation. rows:
// {area, rag, action}. Zebra like the dashboard.
export function outlookTable(rows = []) {
  const W = V3.pageWidthDxa;
  const cA = Math.round(W * 0.42); const cC = Math.round(W * 0.24); const cD = W - cA - cC;
  const widths = [cA, cC, cD];
  const hdr = headerRow(['Area', 'Current Condition', 'Recommendation'], widths);
  const body = (rows || []).map((r, i) => {
    const t = RAG_TOKENS[r.rag];
    const z = i % 2 === 1 ? V3.zebra : undefined;
    return row([
      cell(para(run(r.area || '', { sz: 18, color: V3.ink })), { w: cA, shd: z, valign: 'center' }),
      cell(para(run(t ? t.label : '', { sz: 18, bold: true, color: t ? t.onFill : V3.ink }), { align: 'center' }),
        { w: cC, shd: t ? t.fill : z, valign: 'center' }),
      cell(para(run(r.action || '', { sz: 18, color: V3.ink })), { w: cD, shd: z, valign: 'center' }),
    ]);
  });
  return table([hdr, ...body], widths, { borders: borderRows() });
}

// A neutral photo placeholder box (static template only — the real engine swaps in a
// @@PHOTO_n@@ token run here). Grey, centred caption.
export function photoPlaceholder(caption = 'Evidence photo') {
  return run(`[ ${caption} ]`, { sz: 18, italic: true, color: V3.muted });
}

// ── Scope Options — Option 1 (full spec) vs Option 2 (recommended) (P4) ─────

// Area | Element | Option 1 — Full Repaint | Option 2 — Recommended | Why.
// Option 2 cell is tinted V3.benefitTint with V3.benefitInk bold text, matching
// the Good-news callout, so the reduced scope reads as a saving. rows:
// {area, substrate, fullSpec:{key,label}, recommended:{key,label}, rationale}.
export function optionComparisonTable(rows = []) {
  if (!rows.length) return '';
  const W = V3.pageWidthDxa;
  const cArea = Math.round(W * 0.16);
  const cElement = Math.round(W * 0.20);
  const cFull = Math.round(W * 0.18);
  const cRec = Math.round(W * 0.18);
  const widths = [cArea, cElement, cFull, cRec, W - cArea - cElement - cFull - cRec];
  const hdr = headerRow(['Area', 'Element', 'Option 1 — Full Repaint', 'Option 2 — Recommended', 'Why'], widths);
  const body = rows.map((r, i) => {
    const z = i % 2 === 1 ? V3.zebra : undefined;
    return row([
      cell(para(run(r.area || '', { sz: 18, color: V3.ink })), { w: widths[0], shd: z, valign: 'center' }),
      cell(para(run(r.substrate || '', { sz: 18, color: V3.ink })), { w: widths[1], shd: z, valign: 'center' }),
      cell(para(run((r.fullSpec && r.fullSpec.label) || '', { sz: 18, color: V3.ink })), { w: widths[2], shd: z, valign: 'center' }),
      cell(para(run((r.recommended && r.recommended.label) || '', { sz: 18, bold: true, color: V3.benefitInk })),
        { w: widths[3], shd: V3.benefitTint, valign: 'center' }),
      cell(para(run(r.rationale || '', { sz: 17, italic: true, color: V3.muted })), { w: widths[4], shd: z, valign: 'center' }),
    ]);
  });
  return table([hdr, ...body], widths, { borders: borderRows() });
}

// ── Internal Flooring register (§4) ──────────────────────────────────────────

// Floor product | Location | Condition | Recommendation. Condition cell uses the
// RAG fill + onFill treatment from RAG_TOKENS, exactly as dashboardTable does — a
// floor covering with no condition set (rag === null) renders its ragLabel plain.
// A safety-flagged row gets a following full-width sub-row carrying
// "⚠ Safety: <safetyNote>" in V3.orange bold — the word "Safety" must be present,
// never colour alone.
export function flooringTable(rows = []) {
  if (!rows.length) return '';
  const W = V3.pageWidthDxa;
  const cP = Math.round(W * 0.30); const cL = Math.round(W * 0.28); const cC = Math.round(W * 0.18);
  const widths = [cP, cL, cC, W - cP - cL - cC];
  const hdr = headerRow(['Floor product', 'Location', 'Condition', 'Recommendation'], widths);
  const body = [];
  rows.forEach((r, i) => {
    const t = RAG_TOKENS[r.rag];
    const z = i % 2 === 1 ? V3.zebra : undefined;
    body.push(row([
      cell(para(run(r.product || '', { sz: 18, color: V3.ink })), { w: widths[0], shd: z, valign: 'center' }),
      cell(para(run(r.location || '', { sz: 18, color: V3.ink })), { w: widths[1], shd: z, valign: 'center' }),
      cell(para(run(t ? t.label : (r.ragLabel || ''), { sz: 18, bold: !!t, color: t ? t.onFill : V3.ink }), { align: 'center' }),
        { w: widths[2], shd: t ? t.fill : z, valign: 'center' }),
      cell(para(run(r.recommendation || '', { sz: 18, color: V3.ink })), { w: widths[3], shd: z, valign: 'center' }),
    ]));
    if (r.safetyRisk) {
      body.push(row([
        cell(para(run('⚠ Safety: ', { sz: 17, bold: true, color: V3.orange }) + run(r.safetyNote || '', { sz: 17, color: V3.orange })),
          { w: W, gridSpan: 4 }),
      ]));
    }
  });
  return table([hdr, ...body], widths, { borders: borderRows() });
}

// ── Client referral register (P5) ────────────────────────────────────────────

// Area | Element | Repair identified. Header band in V3.slate like the other
// data tables. No RAG chips — referral rows are not condition ratings and must
// not be read as such. rows: {area, substrate, note}.
export function referralTable(rows = []) {
  if (!rows.length) return '';
  const W = V3.pageWidthDxa;
  const cArea = Math.round(W * 0.24); const cElement = Math.round(W * 0.24);
  const widths = [cArea, cElement, W - cArea - cElement];
  const hdr = headerRow(['Area', 'Element', 'Repair identified'], widths);
  const body = rows.map((r, i) => {
    const z = i % 2 === 1 ? V3.zebra : undefined;
    return row([
      cell(para(run(r.area || '', { sz: 18, color: V3.ink })), { w: widths[0], shd: z, valign: 'center' }),
      cell(para(run(r.substrate || '', { sz: 18, color: V3.ink })), { w: widths[1], shd: z, valign: 'center' }),
      cell(para(run(r.note || '', { sz: 18, color: V3.ink })), { w: widths[2], shd: z, valign: 'center' }),
    ]);
  });
  return table([hdr, ...body], widths, { borders: borderRows() });
}

// ── Site Items (§5, "Other Specified Items") ────────────────────────────────

// Counted items — three columns (Item | Qty | Notes), four when showMarkup is set
// (Item | Qty | Per client mark-up | Notes). Zebra tint on alternate rows, matching
// dashboardTable. rows: {label, qty, note, markup?}.
export function siteItemsTable(rows = [], { showMarkup = false } = {}) {
  if (!rows.length) return '';
  const W = V3.pageWidthDxa;
  let widths, labels;
  if (showMarkup) {
    const cItem = Math.round(W * 0.32); const cQty = Math.round(W * 0.16); const cMark = Math.round(W * 0.20);
    widths = [cItem, cQty, cMark, W - cItem - cQty - cMark];
    labels = ['Item', 'Qty', 'Per client mark-up', 'Notes'];
  } else {
    const cItem = Math.round(W * 0.38); const cQty = Math.round(W * 0.20);
    widths = [cItem, cQty, W - cItem - cQty];
    labels = ['Item', 'Qty', 'Notes'];
  }
  const hdr = headerRow(labels, widths);
  const body = rows.map((r, i) => {
    const z = i % 2 === 1 ? V3.zebra : undefined;
    const cells = [
      cell(para(run(r.label || '', { sz: 18, color: V3.ink })), { w: widths[0], shd: z, valign: 'center' }),
      cell(para(run(r.qty || '', { sz: 18, color: V3.ink }), { align: 'center' }), { w: widths[1], shd: z, valign: 'center' }),
    ];
    if (showMarkup) {
      cells.push(cell(para(run(r.markup || '—', { sz: 18, color: V3.ink }), { align: 'center' }), { w: widths[2], shd: z, valign: 'center' }));
    }
    cells.push(cell(para(run(r.note || '', { sz: 18, color: V3.ink })), { w: widths[showMarkup ? 3 : 2], shd: z, valign: 'center' }));
    return row(cells);
  });
  return table([hdr, ...body], widths, { borders: borderRows() });
}

// A sub-table for one row-based item (display boards, half-height doors, misc,
// other counted items). title is rendered as a bold slate caption row spanning
// all columns (the full-width-cell-in-a-multi-column-table idiom already used by
// conditionCard's notes row), then a header row from cols, then the data rows.
export function siteItemsRowTable(title, cols = [], rows = []) {
  if (!rows.length || !cols.length) return '';
  const W = V3.pageWidthDxa;
  const n = cols.length;
  const base = Math.floor(W / n);
  const widths = cols.map((_, i) => (i === n - 1 ? W - base * (n - 1) : base));
  const caption = row([
    cell(para(run(title, { sz: 18, bold: true, color: 'FFFFFF' }), { before: 40, after: 40 }),
      { w: W, gridSpan: n, shd: V3.slate, valign: 'center' }),
  ]);
  const hdr = headerRow(cols.map((c) => c.label), widths);
  const body = rows.map((r, i) => {
    const z = i % 2 === 1 ? V3.zebra : undefined;
    const cells = cols.map((c, ci) => cell(
      para(run(r[c.k] || '', { sz: 18, color: V3.ink }), c.numeric ? { align: 'center' } : undefined),
      { w: widths[ci], shd: z, valign: 'center' }
    ));
    return row(cells);
  });
  return table([caption, hdr, ...body], widths, { borders: borderRows() });
}
